import { AlertTriangle, X, Unlock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky banner at the top of the Plan page surfacing cascade conflicts on
 * locked tasks. Phase 1.2.1 adds two resolve actions per row:
 *
 *  - "Move to {date}"   → unlocked task slides to suggested_start (stays locked)
 *  - "Unlock"            → flips to cascade-mode; next pass moves it automatically
 *  - "Dismiss"           → paper-trail; warning re-appears if violation persists
 */
export default function ConflictBanner({ conflicts, tasks, onDismiss, onResolve }) {
  if (!conflicts || conflicts.length === 0) return null;
  const taskById = Object.fromEntries((tasks || []).map((t) => [t.id, t]));

  return (
    <div
      data-testid="conflict-banner"
      className="border border-rose-900/60 bg-rose-950/30 rounded-md p-4 mb-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-rose-400" />
        <span className="text-overline text-rose-400">
          {conflicts.length} cascade conflict{conflicts.length === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="space-y-2.5">
        {conflicts.map((c) => {
          const target = taskById[c.task_id];
          const pred = taskById[c.predecessor_id];
          return (
            <li
              key={c.id}
              data-testid={`conflict-row-${c.id}`}
              className="flex items-start justify-between gap-3 text-sm rounded-sm bg-rose-950/30 px-3 py-2 border border-rose-900/40"
            >
              <div className="flex-1 min-w-0">
                <div className="text-rose-100/90">
                  <span className="text-zinc-100 font-medium">
                    {target?.name || "Unknown task"}
                  </span>
                  <span className="text-zinc-500"> is locked, but </span>
                  <span className="text-zinc-100 font-medium">
                    {pred?.name || "predecessor"}
                  </span>
                  <span className="text-zinc-500"> wants it to move.</span>
                </div>
                {c.suggested_start && (
                  <div className="text-xs text-rose-300/80 mt-1 font-mono">
                    Suggested start: {c.suggested_start}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {onResolve && c.suggested_start && (
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`conflict-move-${c.id}`}
                    onClick={() => onResolve(c, "move_forward")}
                    className="h-7 px-2.5 bg-rose-500/80 text-zinc-50 hover:bg-rose-500 rounded-sm text-xs"
                    title={`Move task forward to ${c.suggested_start}, keep it locked`}
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Move to {c.suggested_start}
                  </Button>
                )}
                {onResolve && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`conflict-unlock-${c.id}`}
                    onClick={() => onResolve(c, "unlock")}
                    className="h-7 px-2.5 bg-transparent border-rose-700/60 text-rose-200 hover:bg-rose-900/40 hover:text-rose-100 rounded-sm text-xs"
                    title="Unlock the task so cascade can move it automatically"
                  >
                    <Unlock className="w-3 h-3 mr-1" />
                    Unlock
                  </Button>
                )}
                {onDismiss && (
                  <button
                    type="button"
                    data-testid={`conflict-dismiss-${c.id}`}
                    onClick={() => onDismiss(c)}
                    className="p-1 rounded-sm text-rose-300 hover:bg-rose-900/40 hover:text-rose-200 transition-colors"
                    title="Dismiss this warning"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
